import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material';
import { SxProps } from '@mui/system';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { useVisible } from '~/hooks/useVisible';
import { useRemainingBalance } from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/components/RemainingBalance/useRemainingBalance';
import {
  AvailableBalances,
  BalancePool,
} from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/useVacationBalances';

import { useSxStyles } from './useStyles';

type PropsType = {
  availableBalances: AvailableBalances;
};

export const RemainingBalance = (props: PropsType) => {
  const {
    availableBalances: { totalBalance, balancePools },
  } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { isVisible, toggle } = useVisible(false);

  const {
    extraVacationBalancePools,
    vacationBalancePools,
    yearlyVacationsPools,
    vacationBanksPools,
    isExpandListEnabled,
  } = useRemainingBalance({ balancePools });

  const PoolBalance = (props: {
    title: string;
    balance?: number;
    sx?: SxProps;
  }) => {
    const { title, balance, sx } = props;
    if (typeof balance === 'undefined') {
      return <></>;
    }

    return (
      <ListItem sx={{ ...sxStyles.poolBalance, ...sx }}>
        <Box sx={sxStyles.poolName}>
          <TooltippedTruncatedText>{title}</TooltippedTruncatedText>
        </Box>
        <Box sx={sxStyles.poolValue}>{balance}</Box>
      </ListItem>
    );
  };

  const BalancePoolGroup = (props: {
    pools: BalancePool[];
    title?: string;
  }) => {
    const { title, pools } = props;

    return (
      <>
        {pools.length > 0 && !!title && (
          <Divider sx={sxStyles.divider}>{title}</Divider>
        )}
        {pools.map((pool, index) => {
          const poolTitle = !!pool.reasons
            ? `${pool.name} - ${pool.reasons.map((r) => r.name).join(', ')}`
            : pool.name;

          return (
            <PoolBalance
              title={poolTitle}
              balance={pool.balance}
              key={`balance-pool-${index}`}
            />
          );
        })}
      </>
    );
  };

  const TotalBalance = (props: { title: string; balance: number }) => {
    const { title, balance } = props;
    if (isExpandListEnabled) {
      return (
        <ListItemButton
          onClick={toggle}
          sx={{ ...sxStyles.poolBalance, ...sxStyles.totalBalance }}
        >
          <Box sx={sxStyles.poolName}>
            <TooltippedTruncatedText>{title}</TooltippedTruncatedText>
          </Box>
          <Box sx={sxStyles.poolValue}>{balance}</Box>
          {isVisible ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      );
    }

    return (
      <PoolBalance title={title} balance={balance} sx={sxStyles.totalBalance} />
    );
  };

  return (
    <List sx={sxStyles.root}>
      <TotalBalance
        title={t<string>('myVacationBalance')}
        balance={totalBalance}
      />
      {isExpandListEnabled && (
        <Collapse in={isVisible} unmountOnExit={true}>
          <List>
            <BalancePoolGroup pools={vacationBalancePools} />
            <BalancePoolGroup pools={extraVacationBalancePools} />
            <BalancePoolGroup
              pools={yearlyVacationsPools}
              title={t('yearlyVacationBalances')}
            />
            <BalancePoolGroup
              pools={vacationBanksPools}
              title={t('vacationBanks')}
            />
          </List>
        </Collapse>
      )}
    </List>
  );
};
